<template>
  <div class="restaurant-detail-page">
    <!-- Load restaurant detail on mount -->
    <VBusHelper
      :emitOnMounted="$constants.EVENTS.RESTAURANTS.DETAIL"
      :eventPayload="{ id: $route.params.id }"
    />

    <!-- Loading State -->
    <div v-if="$stores.restaurants.state.detail.loading" class="loading">
      <div class="spinner"></div>
      <p>Chargement du restaurant...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="$stores.restaurants.state.detail.error" class="error">
      <p>{{ $stores.restaurants.state.detail.error }}</p>
      <button @click="retry" class="retry-button">Réessayer</button>
    </div>

    <!-- Restaurant Detail -->
    <div v-else-if="restaurant" class="restaurant-detail">
      <!-- Header Image -->
      <div class="header-image">
        <ImgComponent :src="restaurant.image" :alt="restaurant.name" />
        <button @click="goBack" class="back-button">← Retour</button>
      </div>

      <!-- Info Section -->
      <div class="info-section">
        <div class="header-info">
          <h1 class="restaurant-name">{{ restaurant.name }}</h1>
          <p class="description">{{ restaurant.description }}</p>

          <div class="meta">
            <div class="rating">
              <span class="star">★</span>
              <span class="rating-value">{{ restaurant.rating }}</span>
              <span class="review-count"
                >({{ restaurant.reviewCount }} avis)</span
              >
            </div>

            <div class="price-range">
              <span v-for="i in restaurant.priceRange" :key="i">€</span>
            </div>

            <div
              class="status-badge"
              :class="{ open: restaurant.isOpen, closed: !restaurant.isOpen }"
            >
              {{ restaurant.isOpen ? 'Ouvert' : 'Fermé' }}
            </div>
          </div>

          <div class="tags">
            <span v-for="tag in restaurant.cuisine" :key="tag" class="tag">
              {{ tag }}
            </span>
            <span v-for="tag in restaurant.tags" :key="tag" class="tag">
              {{ tag }}
            </span>
          </div>
        </div>

        <!-- Delivery Info -->
        <div class="delivery-info">
          <div class="info-item">
            <span class="label">Livraison</span>
            <span class="value">{{ restaurant.deliveryTime }}</span>
          </div>
          <div class="info-item">
            <span class="label">Frais de livraison</span>
            <span class="value">{{ restaurant.deliveryFee }} FCFA</span>
          </div>
          <div class="info-item">
            <span class="label">Commande minimum</span>
            <span class="value">{{ restaurant.minOrderAmount }} FCFA</span>
          </div>
        </div>

        <!-- Address -->
        <div class="address">
          <h3>Adresse</h3>
          <p>{{ restaurant.address.street }}</p>
          <p>{{ restaurant.address.city }}, {{ restaurant.address.country }}</p>
        </div>

        <!-- Opening Hours -->
        <div class="opening-hours">
          <h3>Heures d'ouverture</h3>
          <div v-if="restaurant.openingHours" class="hours-list">
            <div
              v-for="(hours, day) in restaurant.openingHours"
              :key="day"
              class="hours-item"
            >
              <span class="day">{{ day }}</span>
              <span class="hours">{{ hours }}</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="actions">
          <button @click="viewMenu" class="view-menu-button">
            Voir le menu
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const $route = useRoute();

const restaurant = computed(() => {
  return window.$stores?.restaurants?.state?.detail?.restaurant;
});

const retry = () => {
  window.$bus?.emit(window.$constants?.EVENTS?.RESTAURANTS?.DETAIL, {
    id: $route.params.id
  });
};

const goBack = () => {
  window.$router?.back();
};

const viewMenu = () => {
  window.$router?.push(`/restaurants/${$route.params.id}/menu`);
};
</script>

<style scoped>
.restaurant-detail-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading,
.error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f59e0b;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  color: #dc2626;
}

.retry-button {
  margin-top: 16px;
  padding: 10px 20px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.header-image {
  position: relative;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.header-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.back-button {
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

.info-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-info {
  margin-bottom: 24px;
}

.restaurant-name {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.description {
  color: #666;
  font-size: 1em;
  line-height: 1.6;
  margin-bottom: 16px;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
  align-items: center;
}

.rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.star {
  color: #f59e0b;
  font-size: 1.2em;
}

.rating-value {
  font-weight: 600;
  font-size: 1.1em;
}

.review-count {
  color: #666;
}

.price-range {
  color: #f59e0b;
  font-weight: 600;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 500;
  font-size: 0.9em;
}

.status-badge.open {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.closed {
  background: #fee2e2;
  color: #991b1b;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  background: #f3f4f6;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.9em;
  color: #4b5563;
}

.delivery-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  padding: 20px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 0.85em;
  color: #666;
}

.value {
  font-weight: 600;
  color: #1a1a1a;
}

.address,
.opening-hours {
  margin-bottom: 24px;
}

.address h3,
.opening-hours h3 {
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.address p {
  color: #666;
  margin: 4px 0;
}

.hours-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hours-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.day {
  font-weight: 500;
  color: #4b5563;
}

.hours {
  color: #666;
}

.actions {
  display: flex;
  gap: 12px;
}

.view-menu-button {
  flex: 1;
  padding: 14px 24px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1em;
  cursor: pointer;
  transition: background 0.2s;
}

.view-menu-button:hover {
  background: #e0890b;
}
</style>
